import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem, FIG_LISTS } from '../../api/figApi';
import generateACORD125 from './generateACORD125';
import { DEFAULT_TRANSACTION_STATUS } from './acordTransactionStatus';
import { normalizeToIsoDate } from '../../utils/dateNormalize';
import { isCurrentPolicy, currentFirst, pickCurrentForLOB } from '../../utils/policySelect';
import './FIG.css';
import './acord/acordTokens.css';

const ENTITY_TYPES = [
  'Corporation', 'Individual', 'LLC', 'Partnership',
  'Joint Venture', 'Not For Profit Org', 'Subchapter S Corporation',
  'Trust', 'Other',
];

function addOneYear(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return '';
  return `${Number(y) + 1}-${m}-${d}`;
}

const NATURE_OF_BUSINESS_TYPES = [
  'Manufacturing', 'Contractor', 'Retail', 'Wholesale', 'Service',
  'Office', 'Restaurant', 'Apartments', 'Condominiums', 'Institutional',
];

// Standard ACORD 125 (2025/03) Lines of Business — alphabetical, hand
// coded directly in the JSX (three `.acord-lob-column` siblings each
// containing seven `<LobRow />` elements). No constant array drives the
// rendering; the column-major arrangement is set in source order.
// TODO (state migration): legacy SharePoint records may store LOB strings
// in older mixed-case forms (e.g. "Commercial Property", "Commercial
// Crime", "Commercial Auto", "Garage"). Those rows won't show as
// pre-checked under the new ACORD-correct labels until a backfill
// renames them. Tracked separately from this visual pass.

// Standard ACORD 125 (2025/03) Attachment supplements — alphabetical, also
// row-first 3-col display (cols 1/2/3 × 10 rows). Six trailing blanks.
// TODO (state): attachment selection is not yet wired to backend state; these
// checkboxes are visual placeholders. Add ATTACHMENTS state alongside LOBs
// in a separate prompt.
const ATTACHMENTS = [
  'ACCOUNTS RECEIVABLE / VALUABLE PAPERS',     'GLASS AND SIGN SECTION',                       'STATEMENT / SCHEDULE OF VALUES',
  'ADDITIONAL INTEREST SCHEDULE',              'HOTEL / MOTEL SUPPLEMENT',                     'STATE SUPPLEMENT (if applicable)',
  'ADDITIONAL PREMISES INFORMATION SCHEDULE',  'INSTALLATION / BUILDERS RISK SECTION',         'VACANT BUILDING SUPPLEMENT',
  'APARTMENT BUILDING SUPPLEMENT',             'INTERNATIONAL LIABILITY EXPOSURE SUPPLEMENT',  'VEHICLE SCHEDULE',
  'CONDO ASSN BYLAWS (for D&O Coverage only)', 'INTERNATIONAL PROPERTY EXPOSURE SUPPLEMENT',   '',
  'CONTRACTORS SUPPLEMENT',                    'LOSS SUMMARY',                                 '',
  'COVERAGES SCHEDULE',                        'OPEN CARGO SECTION',                           '',
  'DEALERS SECTION',                           'PREMIUM PAYMENT SUPPLEMENT',                   '',
  'DRIVER INFORMATION SCHEDULE',               'PROFESSIONAL LIABILITY SUPPLEMENT',            '',
  'ELECTRONIC DATA PROCESSING SECTION',        'RESTAURANT / TAVERN SUPPLEMENT',               '',
];

function ACORD125Page1({ insured, getToken, onBack }) {
  // --- Agency data ---
  const [agency, setAgency] = useState(null);
  const [agencyId, setAgencyId] = useState(null);
  const [agencyForm, setAgencyForm] = useState({
    AgencyName: '', AgencyAddress: '', AgencyCity: '', AgencyState: '', AgencyZip: '',
    ContactName: '', ContactPhone: '', ContactFax: '', ContactEmail: '',
    ProducerCode: '', SubCode: '', AgencyCustomerId: '',
    ProducerName: '', NationalProducerNumber: '', StateLicenseNumber: '',
  });

  // --- Policies data ---
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyIdx, setSelectedPolicyIdx] = useState(0);
  const [policyForm, setPolicyForm] = useState({
    Carrier: '', NAICCode: '', PolicyNumber: '', ProgramName: '', ProgramCode: '',
    Underwriter: '', UnderwriterOffice: '', Status: '',
    EffectiveDate: '', ExpirationDate: '',
  });

  // --- Insured form (applicant + policy info fields from FIG_Insureds) ---
  const [insuredForm, setInsuredForm] = useState({
    CompanyName: insured.CompanyName || '',
    MailingAddress: insured.MailingAddress || '',
    MailingCity: insured.MailingCity || '',
    MailingState: insured.MailingState || '',
    MailingZip: insured.MailingZip || '',
    GLCode: insured.GLCode || '',
    SICCode: insured.SICCode || '',
    NAICSCode: insured.NAICSCode || '',
    FEIN: insured.FEIN || '',
    BusinessPhone: insured.BusinessPhone || '',
    WebsiteAddress: insured.WebsiteAddress || '',
    EntityType: insured.EntityType || '',
    NatureOfBusiness: insured.NatureOfBusiness || '',
    DateBusinessStarted: insured.DateBusinessStarted || '',
    OperationsDescription: insured.OperationsDescription || '',
  });

  // --- Transaction status ---
  // UI-only state — NOT the SharePoint lifecycle Status field. Defaults to
  // 'Renew' because the vast majority of workflows land here; user can switch.
  const [transactionStatus, setTransactionStatus] = useState(DEFAULT_TRANSACTION_STATUS);

  // --- Lines of Business checkboxes (editable local state) ---
  const [checkedLOBs, setCheckedLOBs] = useState(new Set());

  // --- Page 2 data ---
  const [otherNamedInsureds, setOtherNamedInsureds] = useState([]);
  const [contactForms, setContactForms] = useState([]);
  const [buildingForms, setBuildingForms] = useState([]);

  // --- Page 3 data ---
  const [allAdditionalInterests, setAllAdditionalInterests] = useState([]);
  const [aiForms, setAiForms] = useState([]);
  const [gqForms, setGqForms] = useState({});

  // --- Page 4 data ---
  const [carrierForms, setCarrierForms] = useState([]);
  const [lossForms, setLossForms] = useState([]);
  const [noLosses, setNoLosses] = useState(false);
  const [lossYears, setLossYears] = useState('5');
  const [remarks, setRemarks] = useState('');

  // --- Page navigation ---
  const [currentPage, setCurrentPage] = useState(1);

  // --- Save state ---
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const token = await getToken();
      const [agencyItems, policyItems, buildingItems, contactItems, aiItems, gqItems, chItems, lhItems] = await Promise.all([
        getItems(token, FIG_LISTS.AGENCY),
        getItems(token, FIG_LISTS.POLICIES),
        getItems(token, FIG_LISTS.BUILDINGS),
        getItems(token, FIG_LISTS.CONTACTS),
        getItems(token, FIG_LISTS.ADDITIONAL_INTERESTS),
        getItems(token, FIG_LISTS.GENERAL_QUESTIONS),
        getItems(token, FIG_LISTS.CARRIER_HISTORY),
        getItems(token, FIG_LISTS.LOSS_HISTORY),
      ]);

      // Agency — use first record if exists
      if (agencyItems.length > 0) {
        const a = agencyItems[0];
        setAgency(a);
        setAgencyId(a.id);
        setAgencyForm({
          AgencyName: a.AgencyName || '',
          AgencyAddress: a.AgencyAddress || '',
          AgencyCity: a.AgencyCity || '',
          AgencyState: a.AgencyState || '',
          AgencyZip: a.AgencyZip || '',
          ContactName: a.ContactName || '',
          ContactPhone: a.ContactPhone || '',
          ContactFax: a.ContactFax || '',
          ContactEmail: a.ContactEmail || '',
          ProducerCode: a.ProducerCode || '',
          SubCode: a.SubCode || '',
          AgencyCustomerId: a.AgencyCustomerId || '',
          ProducerName: a.ProducerName || '',
          NationalProducerNumber: a.NationalProducerNumber || '',
          StateLicenseNumber: a.StateLicenseNumber || '',
        });
      }

      // Policies for this insured
      const insuredPolicies = policyItems.filter(
        p => String(p.InsuredId) === String(insured.id)
      );
      setPolicies(insuredPolicies);
      // Auto-checks the LOBs of current (Active/Bound) policies. Multi-LOB is
      // the actual workflow; shouldPopulateTargetCarrier gates carrier-header
      // semantics correctly. User can manually uncheck for single-LOB
      // submissions. Prior-term history rows (Expired) never seed the form.
      if (insuredPolicies.length > 0) {
        const firstPolicy = currentFirst(insuredPolicies)[0];
        loadPolicyForm(firstPolicy);
        const currentLobs = insuredPolicies.filter(isCurrentPolicy).map(p => p.LOB).filter(Boolean);
        setCheckedLOBs(new Set(currentLobs));
      } else {
        setCheckedLOBs(new Set());
      }

      // Page 2: Buildings, Contacts, Additional Interests
      const iid = String(insured.id);
      const myBuildings = buildingItems.filter(b => String(b.InsuredId) === iid);
      setBuildingForms(myBuildings.map(b => ({ ...b })));

      const myContacts = contactItems.filter(c => String(c.InsuredId) === iid);
      setContactForms(myContacts.map(c => ({ ...c })));

      const myAI = aiItems.filter(
        ai => String(ai.InsuredId) === iid && (ai.InterestType || '').toLowerCase() === 'other named insured'
      );
      setOtherNamedInsureds(myAI);

      // Page 3: All additional interests + general questions
      const allAI = aiItems.filter(ai => String(ai.InsuredId) === iid);
      setAllAdditionalInterests(allAI);
      setAiForms(allAI.filter(ai => (ai.InterestType || '').toLowerCase() !== 'other named insured').map(ai => ({ ...ai })));

      const myGQ = gqItems.filter(q => String(q.InsuredId) === iid);
      const gqMap = {};
      for (const q of myGQ) {
        gqMap[q.QuestionNumber] = { id: q.id, Answer: q.Answer || '', Explanation: q.Explanation || '' };
      }
      setGqForms(gqMap);

      // Page 4: Carrier history, loss history, remarks
      const myCH = chItems.filter(c => String(c.InsuredId) === iid);
      setCarrierForms(myCH.map(c => ({ ...c })));

      const myLH = lhItems.filter(l => String(l.InsuredId) === iid);
      setLossForms(myLH.map(l => ({ ...l })));
      setNoLosses(myLH.length === 0);

      // Remarks from general questions
      if (gqMap['REMARKS']) {
        setRemarks(gqMap['REMARKS'].Explanation || '');
      }
    } catch (err) {
      console.error('[ACORD125] Load error:', err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, insured.id]);

  useEffect(() => { loadData(); }, [loadData]);

  function loadPolicyForm(p) {
    // SharePoint stores dates as MM/DD/YYYY strings (text column, not DateTime).
    // Normalize to ISO so <input type="date"> can display them.
    const isoExp = normalizeToIsoDate(p.ExpirationDate);
    // Forward-looking term: proposed effective = expiring term's expiration,
    // proposed expiration = +1 year. User can override via the date inputs.
    const proposedEff = isoExp;
    const proposedExp = addOneYear(proposedEff);
    setPolicyForm({
      Carrier: p.Carrier || '',
      NAICCode: p.NAICCode || '',
      PolicyNumber: p.PolicyNumber || '',
      ProgramName: p.ProgramName || '',
      ProgramCode: p.ProgramCode || '',
      Underwriter: p.Underwriter || '',
      UnderwriterOffice: p.UnderwriterOffice || '',
      Status: p.Status || '',
      EffectiveDate: proposedEff,
      ExpirationDate: proposedExp,
    });
  }

  function handlePolicySelect(idx) {
    setSelectedPolicyIdx(idx);
    const p = policies[idx];
    if (p) {
      loadPolicyForm(p);
      // Switching the selected policy changes only the header (carrier,
      // policy#, dates) — not the LOB scope. Leave checkedLOBs alone so
      // the user's portfolio selection survives the switch.
    }
  }

  const isQuote = transactionStatus === 'Quote';

  function handleStatusChange(status) {
    setTransactionStatus(status);
    if (status === 'Quote') {
      // Clear carrier fields — keep dates and billing
      setPolicyForm(f => ({
        ...f,
        Carrier: '', NAICCode: '', PolicyNumber: '',
        ProgramName: '', ProgramCode: '',
        Underwriter: '', UnderwriterOffice: '',
      }));
    } else {
      // Restore from the selected policy record
      const p = policies[selectedPolicyIdx];
      if (p) {
        setPolicyForm(f => ({
          ...f,
          Carrier: p.Carrier || '', NAICCode: p.NAICCode || '',
          PolicyNumber: p.PolicyNumber || '',
          ProgramName: p.ProgramName || '', ProgramCode: p.ProgramCode || '',
          Underwriter: p.Underwriter || '', UnderwriterOffice: p.UnderwriterOffice || '',
        }));
      }
    }
  }

  function toggleLOB(lob) {
    setCheckedLOBs(prev => {
      const next = new Set(prev);
      if (next.has(lob)) next.delete(lob);
      else next.add(lob);
      return next;
    });
  }

  function updateContact(idx, key, value) {
    setContactForms(prev => prev.map((c, i) => i === idx ? { ...c, [key]: value } : c));
  }

  function updateBuilding(idx, key, value) {
    setBuildingForms(prev => prev.map((b, i) => i === idx ? { ...b, [key]: value } : b));
  }

  function updateAI(idx, key, value) {
    setAiForms(prev => prev.map((ai, i) => i === idx ? { ...ai, [key]: value } : ai));
  }

  function updateGQ(qNum, key, value) {
    setGqForms(prev => ({
      ...prev,
      [qNum]: { ...(prev[qNum] || { Answer: '', Explanation: '' }), [key]: value },
    }));
  }

  function updateCarrier(idx, key, value) {
    setCarrierForms(prev => prev.map((c, i) => i === idx ? { ...c, [key]: value } : c));
  }

  function updateLoss(idx, key, value) {
    setLossForms(prev => prev.map((l, i) => i === idx ? { ...l, [key]: value } : l));
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      const token = await getToken();
      const saves = [];

      // Save insured fields
      saves.push(updateItem(token, FIG_LISTS.INSUREDS, insured.id, insuredForm));

      // Save agency
      if (agencyId) {
        saves.push(updateItem(token, FIG_LISTS.AGENCY, agencyId, agencyForm));
      }

      // Save selected policy.
      // SharePoint Status is the lifecycle enum — NOT the ACORD transaction
      // status. Preserve whatever was loaded; do not overwrite from UI.
      // In non-Renew mode, Carrier/NAIC/PolicyNumber may be blanked in local
      // state by the Quote toggle — fall back to the original record so
      // clicking Save doesn't wipe those fields in SharePoint.
      // EffectiveDate/ExpirationDate are EXCLUDED from the write — policyForm
      // holds the forward-rolled PROPOSED-RENEWAL dates (a UI/PDF artifact);
      // SharePoint must keep the current/expiring term. A separate bind-renewal
      // workflow will update SharePoint dates when the new policy is bound.
      if (policies[selectedPolicyIdx]) {
        const original = policies[selectedPolicyIdx];
        const { EffectiveDate: _eff, ExpirationDate: _exp, ...policyFields } = policyForm;
        if (transactionStatus !== 'Renew') {
          policyFields.Carrier = policyForm.Carrier || original.Carrier || '';
          policyFields.NAICCode = policyForm.NAICCode || original.NAICCode || '';
          policyFields.PolicyNumber = policyForm.PolicyNumber || original.PolicyNumber || '';
        }
        saves.push(updateItem(token, FIG_LISTS.POLICIES, original.id, policyFields));
      }

      // Save contacts
      for (const c of contactForms) {
        if (c.id) {
          const { id, InsuredId, ...fields } = c;
          saves.push(updateItem(token, FIG_LISTS.CONTACTS, id, fields));
        }
      }

      // Save buildings
      for (const b of buildingForms) {
        if (b.id) {
          const { id, InsuredId, ...fields } = b;
          saves.push(updateItem(token, FIG_LISTS.BUILDINGS, id, fields));
        }
      }

      // Save additional interests
      for (const ai of aiForms) {
        if (ai.id) {
          const { id, InsuredId, PolicyId, ...fields } = ai;
          saves.push(updateItem(token, FIG_LISTS.ADDITIONAL_INTERESTS, id, fields));
        }
      }

      // Save carrier history
      for (const c of carrierForms) {
        if (c.id) {
          const { id, InsuredId, ...fields } = c;
          saves.push(updateItem(token, FIG_LISTS.CARRIER_HISTORY, id, fields));
        }
      }

      // Save loss history
      for (const l of lossForms) {
        if (l.id) {
          const { id, InsuredId, ...fields } = l;
          saves.push(updateItem(token, FIG_LISTS.LOSS_HISTORY, id, fields));
        }
      }

      // Save remarks as general question
      if (remarks) {
        updateGQ('REMARKS', 'Explanation', remarks);
      }

      // Save general questions
      for (const [qNum, q] of Object.entries(gqForms)) {
        if (q.id) {
          saves.push(updateItem(token, FIG_LISTS.GENERAL_QUESTIONS, q.id, {
            Answer: q.Answer, Explanation: q.Explanation,
          }));
        } else if (q.Answer || q.Explanation) {
          saves.push(createItem(token, FIG_LISTS.GENERAL_QUESTIONS, {
            Title: `Q${qNum}`,
            InsuredId: String(insured.id),
            FormType: 'ACORD125',
            QuestionNumber: qNum,
            QuestionText: '',
            Answer: q.Answer,
            Explanation: q.Explanation,
          }));
        }
      }

      await Promise.all(saves);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="acord-form">
        <p className="FIG-loading">Loading ACORD 125...</p>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const todayMMDDYYYY = (() => {
    const [y, m, d] = today.split('-');
    return `${m}/${d}/${y}`;
  })();

  const navBtnStyle = (disabled) => ({
    width: '40px', height: '40px', minWidth: '40px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.2rem', fontWeight: 700,
    background: disabled ? '#555' : '#7eb8da',
    color: '#fff', border: 'none', borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    fontFamily: 'inherit',
  });

  const pageNav = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <button style={navBtnStyle(currentPage === 1)}
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
        aria-label="Previous page">&lsaquo;</button>
      <span className="acord-page-label">Page {currentPage} of 5</span>
      <button style={navBtnStyle(currentPage === 5)}
        disabled={currentPage === 5}
        onClick={() => setCurrentPage(p => Math.min(5, p + 1))}
        aria-label="Next page">&rsaquo;</button>
    </div>
  );

  // Hand-coded ACORD 125 LOB layout — three columns, 7 rows each.
  // Column-major alphabetical order is set in source. Empty strings
  // pad column 3 below YACHT to keep all three columns 7 rows tall.
  const LOB_COLUMNS = [
    ['BOILER & MACHINERY', 'BUSINESS AUTO', 'BUSINESS OWNERS', 'COMMERCIAL GENERAL LIABILITY', 'COMMERCIAL INLAND MARINE', 'COMMERCIAL PROPERTY', 'CRIME'],
    ['CYBER AND PRIVACY', 'FIDUCIARY LIABILITY', 'GARAGE AND DEALERS', 'LIQUOR LIABILITY', 'MOTOR CARRIER', 'TRUCKERS', 'UMBRELLA'],
    ['YACHT', '', '', '', '', '', ''],
  ];

  // Display-only premium lookup. Newest current (Active/Bound) policy for the
  // LOB, case-insensitive; returns the formatted Premium amount or ''.
  // generateACORD125.js still populates premium fields on output PDFs
  // from policies[].Premium independently via lobPremiumMap.
  const getPremiumForLOB = (label) => {
    const match = pickCurrentForLOB(policies, label);
    return match && match.Premium
      ? Number(match.Premium).toLocaleString()
      : '';
  };

  // POLICY PREMIUM box: current rows only. A current row with no Premium is
  // left out (never counted as 0); the tooltip says how many were excluded.
  const currentPolicies = policies.filter(isCurrentPolicy);
  const premiumRows = currentPolicies.filter(p => p.Premium != null && p.Premium !== '' && !Number.isNaN(Number(p.Premium)));
  const premiumTotal = premiumRows.reduce((sum, p) => sum + Number(p.Premium), 0);
  const premiumMissing = currentPolicies.length - premiumRows.length;
  const premiumTitle = `${premiumRows.length} of ${currentPolicies.length} current policies summed`
    + (premiumMissing ? `; ${premiumMissing} without a premium excluded` : '');

  return (
    <div className="acord-form">
      {/* Toolbar — non-ACORD widgets only (page nav, policy switcher).
          Anything inside .acord-page must look like the printed form. */}
      <div className="acord-toolbar">
        {pageNav}
        {policies.length > 1 && (
          <select className="acord-policy-select" value={selectedPolicyIdx}
            onChange={e => handlePolicySelect(Number(e.target.value))}>
            {policies.map((p, i) => (
              <option key={p.id} value={i}>
                {p.LOB || 'Policy'} — {p.PolicyNumber || '(no number)'}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Legacy header — kept for pages 3–5 until they migrate to .acord-page.
          Pages 1 and 2 now render their own ACORD-style header inside the
          .acord-page wrapper. */}
      {currentPage > 2 && (
        <div className="acord-header">
          <div className="acord-header-left">
            <span className="acord-logo">ACORD</span>
            <span className="acord-form-title">ACORD 125 &mdash; COMMERCIAL INSURANCE APPLICATION</span>
          </div>
          <div className="acord-header-right">
            <label className="acord-hdr-field">
              <span className="acord-hdr-label">DATE (MM/DD/YYYY)</span>
              <span className="acord-hdr-value">{today}</span>
            </label>
          </div>
        </div>
      )}

      {currentPage === 1 && (
      <div className="acord-page-frame">
      <div className="acord-page">
        {/* ============ PAGE HEADER ============ */}
        <div className="acord-page-header">
          <div className="acord-page-header__logo">
            {/* TODO: replace text placeholder with real ACORD logo SVG asset. */}
            <span className="acord-page-logo">ACORD</span>
          </div>
          <div className="acord-page-header__titles">
            <div className="acord-form-title">COMMERCIAL INSURANCE APPLICATION</div>
            <div className="acord-form-subtitle">APPLICANT INFORMATION SECTION</div>
          </div>
          <div className="acord-page-header__date">
            <div className="acord-cell acord-cell--fillable">
              <span className="acord-cell-label">DATE (MM/DD/YYYY)</span>
              <span className="acord-input">{todayMMDDYYYY}</span>
            </div>
          </div>
        </div>

        {/* ============ PRODUCER + CARRIER (2-col) ============ */}
        <div className="acord-grid acord-grid--2col">
          {/* PRODUCER */}
          <div>
            <div className="acord-section-header">PRODUCER</div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">AGENCY</span>
                <input className="acord-input" value={agencyForm.AgencyName}
                  onChange={e => setAgencyForm(f => ({ ...f, AgencyName: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">CONTACT NAME</span>
                <input className="acord-input" value={agencyForm.ContactName}
                  onChange={e => setAgencyForm(f => ({ ...f, ContactName: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">MAILING ADDRESS</span>
                <input className="acord-input" value={agencyForm.AgencyAddress}
                  onChange={e => setAgencyForm(f => ({ ...f, AgencyAddress: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 2 }}>
                <span className="acord-cell-label">CITY</span>
                <input className="acord-input" value={agencyForm.AgencyCity}
                  onChange={e => setAgencyForm(f => ({ ...f, AgencyCity: e.target.value }))} />
              </label>
              <label className="acord-cell acord-cell--fillable" style={{ flex: 0.6 }}>
                <span className="acord-cell-label">STATE</span>
                <input className="acord-input" value={agencyForm.AgencyState} maxLength={2}
                  onChange={e => setAgencyForm(f => ({ ...f, AgencyState: e.target.value }))} />
              </label>
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">ZIP</span>
                <input className="acord-input" value={agencyForm.AgencyZip}
                  onChange={e => setAgencyForm(f => ({ ...f, AgencyZip: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">PHONE (A/C, No, Ext)</span>
                <input className="acord-input" value={agencyForm.ContactPhone}
                  onChange={e => setAgencyForm(f => ({ ...f, ContactPhone: e.target.value }))} />
              </label>
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">FAX (A/C, No)</span>
                <input className="acord-input" value={agencyForm.ContactFax}
                  onChange={e => setAgencyForm(f => ({ ...f, ContactFax: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">E-MAIL ADDRESS</span>
                <input className="acord-input" value={agencyForm.ContactEmail}
                  onChange={e => setAgencyForm(f => ({ ...f, ContactEmail: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">CODE</span>
                <input className="acord-input" value={agencyForm.ProducerCode}
                  onChange={e => setAgencyForm(f => ({ ...f, ProducerCode: e.target.value }))} />
              </label>
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">SUB CODE</span>
                <input className="acord-input" value={agencyForm.SubCode}
                  onChange={e => setAgencyForm(f => ({ ...f, SubCode: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">AGENCY CUSTOMER ID</span>
                <input className="acord-input" value={agencyForm.AgencyCustomerId}
                  onChange={e => setAgencyForm(f => ({ ...f, AgencyCustomerId: e.target.value }))} />
              </label>
            </div>
          </div>

          {/* CARRIER */}
          <div>
            <div className="acord-section-header">CARRIER</div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 4 }}>
                <span className="acord-cell-label">CARRIER</span>
                <input className="acord-input" value={policyForm.Carrier}
                  onChange={e => setPolicyForm(f => ({ ...f, Carrier: e.target.value }))} />
              </label>
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">NAIC CODE</span>
                <input className="acord-input" value={policyForm.NAICCode}
                  onChange={e => setPolicyForm(f => ({ ...f, NAICCode: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 3 }}>
                <span className="acord-cell-label">COMPANY POLICY OR PROGRAM NAME</span>
                <input className="acord-input" value={policyForm.ProgramName}
                  onChange={e => setPolicyForm(f => ({ ...f, ProgramName: e.target.value }))} />
              </label>
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">PROGRAM CODE</span>
                <input className="acord-input" value={policyForm.ProgramCode}
                  onChange={e => setPolicyForm(f => ({ ...f, ProgramCode: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">POLICY NUMBER</span>
                <input className="acord-input" value={policyForm.PolicyNumber}
                  onChange={e => setPolicyForm(f => ({ ...f, PolicyNumber: e.target.value }))} />
              </label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">UNDERWRITER</span>
                <input className="acord-input" value={policyForm.Underwriter}
                  onChange={e => setPolicyForm(f => ({ ...f, Underwriter: e.target.value }))} />
              </label>
              <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                <span className="acord-cell-label">UNDERWRITER OFFICE</span>
                <input className="acord-input" value={policyForm.UnderwriterOffice}
                  onChange={e => setPolicyForm(f => ({ ...f, UnderwriterOffice: e.target.value }))} />
              </label>
            </div>

            {/* STATUS OF TRANSACTION — 5-col × 4-row sub-grid. Every row uses
                the same column template (60px | 1fr | 1fr | 1fr | 80px) so
                radios, inputs, and the AM/PM column line up vertically. Radio
                behavior unchanged: each radio still toggles transactionStatus
                via handleStatusChange with the original string value. The
                date / time / AM-PM placeholders are unbound for now.
                TODO (state): wire boundDate, changeDate, changeTime, and
                changeMeridiem in a future prompt. */}
            <div className="acord-section-header">STATUS OF TRANSACTION</div>
            <div className="acord-status-grid">
              {/* Row 1 — empty | QUOTE | ISSUE POLICY | RENEW | empty */}
              <div className="acord-cell" />
              <label className="acord-cell acord-cell--fillable">
                <span className="acord-cell-label">QUOTE</span>
                <input type="radio" name="transactionStatus"
                  checked={transactionStatus === 'Quote'}
                  onChange={() => handleStatusChange('Quote')} />
              </label>
              <label className="acord-cell acord-cell--fillable">
                <span className="acord-cell-label">ISSUE POLICY</span>
                <input type="radio" name="transactionStatus"
                  checked={transactionStatus === 'Issue Policy'}
                  onChange={() => handleStatusChange('Issue Policy')} />
              </label>
              <label className="acord-cell acord-cell--fillable">
                <span className="acord-cell-label">RENEW</span>
                <input type="radio" name="transactionStatus"
                  checked={transactionStatus === 'Renew'}
                  onChange={() => handleStatusChange('Renew')} />
              </label>
              <div className="acord-cell" />

              {/* Row 2 — BOUND | radio + helper | DATE (span 2) | empty */}
              <div className="acord-cell acord-status-side-label">BOUND</div>
              <label className="acord-cell acord-cell--fillable">
                <div className="acord-inline-checks">
                  <input type="radio" name="transactionStatus"
                    checked={transactionStatus === 'Bound'}
                    onChange={() => handleStatusChange('Bound')} />
                  <span className="acord-status-helper">Give Date and/or Attach Copy:</span>
                </div>
              </label>
              <div className="acord-cell acord-cell--fillable" style={{ gridColumn: 'span 2' }}>
                <span className="acord-cell-label">DATE</span>
                <input className="acord-input" type="date" readOnly />
              </div>
              <div className="acord-cell" />

              {/* Row 3 — CHANGE | radio | DATE | TIME | AM/PM */}
              <div className="acord-cell acord-status-side-label">CHANGE</div>
              <label className="acord-cell acord-cell--fillable">
                <input type="radio" name="transactionStatus"
                  checked={transactionStatus === 'Change'}
                  onChange={() => handleStatusChange('Change')} />
              </label>
              <div className="acord-cell acord-cell--fillable">
                <span className="acord-cell-label">DATE</span>
                <input className="acord-input" type="date" readOnly />
              </div>
              <div className="acord-cell acord-cell--fillable">
                <span className="acord-cell-label">TIME</span>
                <input className="acord-input" type="time" readOnly />
              </div>
              <div className="acord-cell acord-status-ampm">
                <label className="acord-checkbox"><input type="checkbox" readOnly /><span>AM</span></label>
                <label className="acord-checkbox"><input type="checkbox" readOnly /><span>PM</span></label>
              </div>

              {/* Row 4 — CANCEL | radio | empty (span 3) */}
              <div className="acord-cell acord-status-side-label">CANCEL</div>
              <label className="acord-cell acord-cell--fillable">
                <input type="radio" name="transactionStatus"
                  checked={transactionStatus === 'Cancel'}
                  onChange={() => handleStatusChange('Cancel')} />
              </label>
              <div className="acord-cell" style={{ gridColumn: 'span 3' }} />
            </div>
          </div>
        </div>

        {/* ============ NOTICE REGARDING CANCELLATION ============ */}
        <div className="acord-notice" style={{ textAlign: 'center' }}>
          <strong>NOTICE REGARDING CANCELLATION APPLICABLE IN SOUTH CAROLINA:</strong>{' '}
          THE INSURER CAN CANCEL THIS POLICY FOR WHICH YOU ARE APPLYING WITHOUT CAUSE DURING THE FIRST 120 DAYS. THAT IS THE INSURER&apos;S CHOICE. AFTER THE FIRST 120 DAYS, THE INSURER CAN ONLY CANCEL THIS POLICY FOR REASONS STATED IN THE POLICY.
        </div>

        {/* ============ LINES OF BUSINESS (3 hand-coded columns × 7 rows) ============ */}
        <div className="acord-section-header">LINES OF BUSINESS</div>
        <div className="acord-lob-grid">
          {LOB_COLUMNS.map((column, colIdx) => (
            <div key={colIdx} className="acord-lob-column">
              {column.map((label, rowIdx) => (
                <div key={rowIdx} className="acord-lob-row">
                  {label ? (
                    <>
                      <input
                        type="checkbox"
                        className="acord-checkbox"
                        checked={checkedLOBs.has(label)}
                        onChange={() => toggleLOB(label)}
                      />
                      <span className="acord-lob-label">{label}</span>
                      {/* Premium intentionally not displayed in the intake.
                          The cell stays in the DOM to reserve the 60px right
                          slot so the row matches the printed ACORD's shape.
                          generateACORD125.js still writes premium values to
                          the output PDF from policies[].Premium. */}
                      <span className="acord-lob-premium" />
                    </>
                  ) : (
                    <span className="acord-lob-label" />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* ============ ATTACHMENTS (visual only — checkboxes unbound).
            Same pattern as LOB rows: tinted checkbox-cell wrapper around
            the input, white-bg label span next to it. ============ */}
        <div className="acord-section-header">ATTACHMENTS</div>
        <div className="acord-grid acord-grid--3col">
          {ATTACHMENTS.map((att, i) => (
            att ? (
              <div className="acord-lob-cell" key={att}>
                <span className="acord-checkbox-cell">
                  <label className="acord-checkbox">
                    <input type="checkbox" readOnly />
                  </label>
                </span>
                <span className="acord-lob-label">{att}</span>
              </div>
            ) : (
              <div className="acord-lob-cell" key={`att-blank-${i}`} aria-hidden="true">
                <span className="acord-checkbox-cell" />
                <span className="acord-lob-label">&nbsp;</span>
              </div>
            )
          ))}
        </div>

        {/* ============ POLICY INFORMATION (single row matching real PDF) ============ */}
        <div className="acord-section-header">POLICY INFORMATION</div>
        <div className="acord-row">
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">PROPOSED EFF DATE</span>
            <input className="acord-input" type="date" value={policyForm.EffectiveDate}
              onChange={e => setPolicyForm(f => ({ ...f, EffectiveDate: e.target.value }))} />
          </label>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">PROPOSED EXP DATE</span>
            <input className="acord-input" type="date" value={policyForm.ExpirationDate}
              onChange={e => setPolicyForm(f => ({ ...f, ExpirationDate: e.target.value }))} />
          </label>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1.2 }}>
            <span className="acord-cell-label">BILLING PLAN</span>
            <div className="acord-inline-checks">
              <label className="acord-checkbox"><input type="checkbox" readOnly /><span>DIRECT BILL</span></label>
              <label className="acord-checkbox"><input type="checkbox" readOnly /><span>AGENCY BILL</span></label>
            </div>
          </div>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">PAYMENT PLAN</span>
            <input className="acord-input" readOnly />
          </div>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">METHOD OF PAYMENT</span>
            <input className="acord-input" readOnly />
          </div>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 0.5 }}>
            <span className="acord-cell-label">AUDIT</span>
            <input className="acord-input" readOnly />
          </div>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 0.7 }}>
            <span className="acord-cell-label">DEPOSIT</span>
            <input className="acord-input" readOnly />
          </div>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 0.8 }}>
            <span className="acord-cell-label">MIN PREMIUM</span>
            <input className="acord-input" readOnly />
          </div>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">POLICY PREMIUM</span>
            <input className="acord-input" readOnly title={isQuote ? undefined : premiumTitle}
              value={isQuote || premiumRows.length === 0 ? '' : premiumTotal.toLocaleString()} />
          </div>
        </div>

        {/* ============ APPLICANT INFORMATION ============ */}
        <div className="acord-section-header">APPLICANT INFORMATION</div>
        <div className="acord-row">
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">FIRST NAMED INSURED</span>
            <input className="acord-input" value={insuredForm.CompanyName}
              onChange={e => setInsuredForm(f => ({ ...f, CompanyName: e.target.value }))} />
          </label>
        </div>
        <div className="acord-row">
          <label className="acord-cell acord-cell--fillable" style={{ flex: 2 }}>
            <span className="acord-cell-label">MAILING ADDRESS</span>
            <input className="acord-input" value={insuredForm.MailingAddress}
              onChange={e => setInsuredForm(f => ({ ...f, MailingAddress: e.target.value }))} />
          </label>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">CITY</span>
            <input className="acord-input" value={insuredForm.MailingCity}
              onChange={e => setInsuredForm(f => ({ ...f, MailingCity: e.target.value }))} />
          </label>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 0.4 }}>
            <span className="acord-cell-label">STATE</span>
            <input className="acord-input" value={insuredForm.MailingState} maxLength={2}
              onChange={e => setInsuredForm(f => ({ ...f, MailingState: e.target.value }))} />
          </label>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 0.6 }}>
            <span className="acord-cell-label">ZIP</span>
            <input className="acord-input" value={insuredForm.MailingZip}
              onChange={e => setInsuredForm(f => ({ ...f, MailingZip: e.target.value }))} />
          </label>
        </div>
        <div className="acord-row">
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">GL CODE</span>
            <input className="acord-input" value={insuredForm.GLCode}
              onChange={e => setInsuredForm(f => ({ ...f, GLCode: e.target.value }))} />
          </label>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">SIC</span>
            <input className="acord-input" value={insuredForm.SICCode}
              onChange={e => setInsuredForm(f => ({ ...f, SICCode: e.target.value }))} />
          </label>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">NAICS</span>
            <input className="acord-input" value={insuredForm.NAICSCode}
              onChange={e => setInsuredForm(f => ({ ...f, NAICSCode: e.target.value }))} />
          </label>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">FEIN</span>
            <input className="acord-input" value={insuredForm.FEIN}
              onChange={e => setInsuredForm(f => ({ ...f, FEIN: e.target.value }))} />
          </label>
        </div>
        <div className="acord-row">
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">BUSINESS PHONE (A/C, No)</span>
            <input className="acord-input" value={insuredForm.BusinessPhone}
              onChange={e => setInsuredForm(f => ({ ...f, BusinessPhone: e.target.value }))} />
          </label>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">WEBSITE ADDRESS</span>
            <input className="acord-input" value={insuredForm.WebsiteAddress}
              onChange={e => setInsuredForm(f => ({ ...f, WebsiteAddress: e.target.value }))} />
          </label>
        </div>
        <div className="acord-row">
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">ENTITY TYPE</span>
            <div className="acord-inline-checks">
              {ENTITY_TYPES.map(et => (
                // Visual: square X-style checkbox (matches LOB / Attachments).
                // Behavior: radio-like — clicking sets EntityType, so only one
                // is "checked" at a time via the existing onChange handler.
                <label className="acord-checkbox" key={et}>
                  <input type="checkbox"
                    checked={insuredForm.EntityType === et}
                    onChange={() => setInsuredForm(f => ({ ...f, EntityType: et }))} />
                  <span>{et}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ============ FOOTER ============ */}
        <div className="acord-footer-line">
          <span>ACORD 125 (2025/03)</span>
          <span>Page 1 of 5</span>
          <span>© 1993-2025 ACORD CORPORATION. All rights reserved.</span>
        </div>
      </div>
      </div>
      )}

      {currentPage === 2 && (
      <div className="acord-page-frame">
      <div className="acord-page">
        {/* ============ PAGE HEADER ============ */}
        <div className="acord-page-header">
          <div className="acord-page-header__logo">
            {/* TODO: replace text placeholder with real ACORD logo SVG asset. */}
            <span className="acord-page-logo">ACORD</span>
          </div>
          <div className="acord-page-header__titles">
            <div className="acord-form-title">COMMERCIAL INSURANCE APPLICATION</div>
            <div className="acord-form-subtitle">APPLICANT INFORMATION SECTION (Cont'd)</div>
          </div>
          <div className="acord-page-header__date">
            <div className="acord-cell acord-cell--fillable">
              <span className="acord-cell-label">DATE (MM/DD/YYYY)</span>
              <span className="acord-input">{todayMMDDYYYY}</span>
            </div>
          </div>
        </div>

        {/* ============ APPLICANT INFORMATION (CONTINUED) ============ */}
        <div className="acord-section-header">APPLICANT INFORMATION (CONTINUED)</div>
        {(otherNamedInsureds.length > 0 ? otherNamedInsureds : [null]).map((oni, i) => {
          // Read-only display block. Other Named Insureds are managed from
          // the Additional Interests tab; rendering here mirrors the
          // printed-form structure of the real ACORD page 2.
          const data = oni || {};
          const notes = data.Notes || '';
          const feinMatch = notes.match(/FEIN\s+([\d-]+)/i);
          const fein = feinMatch ? feinMatch[1] : '';
          const entityHint = notes.replace(/FEIN\s+[\d-]+\s*/i, '').trim();
          const cityStateZip = [data.City, [data.State, data.Zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
          return (
            <div key={data.id || `oni-${i}`}>
              <div className="acord-row">
                <div className="acord-cell acord-cell--fillable" style={{ flex: 2 }}>
                  <span className="acord-cell-label">NAME (OTHER NAMED INSURED) AND MAILING ADDRESS (INCLUDING ZIP+4)</span>
                  <span className="acord-input">{[data.EntityName, data.Address].filter(Boolean).join(' — ')}</span>
                </div>
                <div className="acord-cell acord-cell--fillable" style={{ flex: 0.6 }}>
                  <span className="acord-cell-label">GL CODE</span>
                  {/* TODO: surface GL code if/when stored on additional interest record. */}
                  <span className="acord-input"></span>
                </div>
                <div className="acord-cell acord-cell--fillable" style={{ flex: 0.6 }}>
                  <span className="acord-cell-label">SIC</span>
                  <span className="acord-input"></span>
                </div>
                <div className="acord-cell acord-cell--fillable" style={{ flex: 0.7 }}>
                  <span className="acord-cell-label">NAICS</span>
                  <span className="acord-input"></span>
                </div>
                <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">FEIN OR SOC SEC #</span>
                  <span className="acord-input">{fein}</span>
                </div>
              </div>
              <div className="acord-row">
                <div className="acord-cell acord-cell--fillable" style={{ flex: 2 }}>
                  <span className="acord-cell-label">CITY / STATE / ZIP</span>
                  <span className="acord-input">{cityStateZip}</span>
                </div>
                <div className="acord-cell acord-cell--fillable" style={{ flex: 1.9 }}>
                  <span className="acord-cell-label">BUSINESS PHONE #</span>
                  <span className="acord-input"></span>
                </div>
              </div>
              <div className="acord-row">
                <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">WEBSITE ADDRESS</span>
                  <span className="acord-input"></span>
                </div>
              </div>
              <div className="acord-row">
                <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">ENTITY TYPE</span>
                  <div className="acord-inline-checks">
                    {ENTITY_TYPES.map(et => (
                      <label className="acord-checkbox" key={et}>
                        <input type="checkbox"
                          checked={entityHint && entityHint.toLowerCase() === et.toLowerCase()}
                          readOnly />
                        <span>{et}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* ============ CONTACT INFORMATION (2-col card grid) ============ */}
        <div className="acord-section-header">CONTACT INFORMATION</div>
        <div className="acord-grid acord-grid--2col">
          {(contactForms.length > 0 ? contactForms : [null]).map((c, idx) => {
            const data = c || {};
            const isPlaceholder = !c;
            const onChange = (key) => (e) => {
              if (isPlaceholder) return;
              updateContact(idx, key, e.target.value);
            };
            return (
              <div key={data.id || `contact-${idx}`}>
                <div className="acord-row">
                  <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                    <span className="acord-cell-label">CONTACT TYPE</span>
                    <input className="acord-input" value={data.ContactType || ''}
                      onChange={onChange('ContactType')} readOnly={isPlaceholder} />
                  </label>
                </div>
                <div className="acord-row">
                  <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                    <span className="acord-cell-label">CONTACT NAME</span>
                    <input className="acord-input" value={data.ContactName || ''}
                      onChange={onChange('ContactName')} readOnly={isPlaceholder} />
                  </label>
                </div>
                <div className="acord-row">
                  <label className="acord-cell acord-cell--fillable" style={{ flex: 1.4 }}>
                    <span className="acord-cell-label">PRIMARY PHONE #</span>
                    <input className="acord-input" value={data.Phone || ''}
                      onChange={onChange('Phone')} readOnly={isPlaceholder} />
                  </label>
                  <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                    <span className="acord-cell-label">TYPE</span>
                    {/* TODO: wire phone-type state when added to FIG_Contacts schema. */}
                    <div className="acord-inline-checks">
                      <label className="acord-checkbox"><input type="checkbox" readOnly /><span>Home</span></label>
                      <label className="acord-checkbox"><input type="checkbox" readOnly /><span>Bus</span></label>
                      <label className="acord-checkbox"><input type="checkbox" readOnly /><span>Cell</span></label>
                    </div>
                  </div>
                </div>
                <div className="acord-row">
                  <div className="acord-cell acord-cell--fillable" style={{ flex: 1.4 }}>
                    <span className="acord-cell-label">SECONDARY PHONE #</span>
                    {/* TODO: wire SecondaryPhone state when added to FIG_Contacts schema. */}
                    <input className="acord-input" readOnly />
                  </div>
                  <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                    <span className="acord-cell-label">TYPE</span>
                    <div className="acord-inline-checks">
                      <label className="acord-checkbox"><input type="checkbox" readOnly /><span>Home</span></label>
                      <label className="acord-checkbox"><input type="checkbox" readOnly /><span>Bus</span></label>
                      <label className="acord-checkbox"><input type="checkbox" readOnly /><span>Cell</span></label>
                    </div>
                  </div>
                </div>
                <div className="acord-row">
                  <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                    <span className="acord-cell-label">PRIMARY E-MAIL ADDRESS</span>
                    <input className="acord-input" value={data.Email || ''}
                      onChange={onChange('Email')} readOnly={isPlaceholder} />
                  </label>
                </div>
                <div className="acord-row">
                  <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                    <span className="acord-cell-label">SECONDARY E-MAIL ADDRESS</span>
                    <input className="acord-input" value={data.SecondaryEmail || ''}
                      onChange={onChange('SecondaryEmail')} readOnly={isPlaceholder} />
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        {/* ============ PREMISES INFORMATION ============ */}
        <div className="acord-section-header">PREMISES INFORMATION&nbsp;&nbsp;(Attach ACORD 823 for Additional Premises)</div>
        {(buildingForms.length > 0 ? buildingForms : [null]).map((b, idx) => {
          const data = b || {};
          const isPlaceholder = !b;
          const onText = (key) => (e) => {
            if (isPlaceholder) return;
            updateBuilding(idx, key, e.target.value);
          };
          const onCheck = (key, value) => () => {
            if (isPlaceholder) return;
            updateBuilding(idx, key, value);
          };
          return (
            <div key={data.id || `bldg-${idx}`}>
              <div className="acord-row">
                <div className="acord-cell acord-cell--fillable" style={{ flex: 0.5 }}>
                  <span className="acord-cell-label">LOC #</span>
                  <span className="acord-input">{idx + 1}</span>
                </div>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 3 }}>
                  <span className="acord-cell-label">STREET</span>
                  <input className="acord-input" value={data.Address || ''}
                    onChange={onText('Address')} readOnly={isPlaceholder} />
                </label>
                <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">CITY LIMITS</span>
                  <div className="acord-inline-checks">
                    <label className="acord-checkbox">
                      <input type="checkbox" checked={data._cityLimits !== 'Outside'}
                        onChange={onCheck('_cityLimits', 'Inside')} />
                      <span>Inside</span>
                    </label>
                    <label className="acord-checkbox">
                      <input type="checkbox" checked={data._cityLimits === 'Outside'}
                        onChange={onCheck('_cityLimits', 'Outside')} />
                      <span>Outside</span>
                    </label>
                  </div>
                </div>
                <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">INTEREST</span>
                  <div className="acord-inline-checks">
                    <label className="acord-checkbox">
                      <input type="checkbox" checked={(data.OwnerOrTenant || '').toLowerCase() === 'owner'}
                        onChange={onCheck('OwnerOrTenant', 'Owner')} />
                      <span>Owner</span>
                    </label>
                    <label className="acord-checkbox">
                      <input type="checkbox" checked={(data.OwnerOrTenant || '').toLowerCase() === 'tenant'}
                        onChange={onCheck('OwnerOrTenant', 'Tenant')} />
                      <span>Tenant</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="acord-row">
                <div className="acord-cell acord-cell--fillable" style={{ flex: 0.5 }}>
                  <span className="acord-cell-label">BLD #</span>
                  <span className="acord-input">{idx + 1}</span>
                </div>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 1.4 }}>
                  <span className="acord-cell-label">CITY</span>
                  <input className="acord-input" value={data.City || ''}
                    onChange={onText('City')} readOnly={isPlaceholder} />
                </label>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 0.5 }}>
                  <span className="acord-cell-label">STATE</span>
                  <input className="acord-input" value={data.State || ''} maxLength={2}
                    onChange={onText('State')} readOnly={isPlaceholder} />
                </label>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 0.7 }}>
                  <span className="acord-cell-label">ZIP</span>
                  <input className="acord-input" value={data.Zip || ''}
                    onChange={onText('Zip')} readOnly={isPlaceholder} />
                </label>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 0.9 }}>
                  <span className="acord-cell-label"># FULL TIME EMP</span>
                  <input className="acord-input" type="number" value={data.FullTimeEmployees ?? ''}
                    onChange={onText('FullTimeEmployees')} readOnly={isPlaceholder} />
                </label>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">ANNUAL REVENUES $</span>
                  <input className="acord-input" type="number" value={data.AnnualRevenues ?? ''}
                    onChange={onText('AnnualRevenues')} readOnly={isPlaceholder} />
                </label>
              </div>
              <div className="acord-row">
                <div className="acord-cell acord-cell--fillable" style={{ flex: 1.4 }}>
                  <span className="acord-cell-label">COUNTY</span>
                  {/* TODO: bind to County field when added to FIG_Buildings schema. */}
                  <input className="acord-input" readOnly />
                </div>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 0.9 }}>
                  <span className="acord-cell-label"># PART TIME EMP</span>
                  <input className="acord-input" type="number" value={data.PartTimeEmployees ?? ''}
                    onChange={onText('PartTimeEmployees')} readOnly={isPlaceholder} />
                </label>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">OCCUPIED AREA SQ FT</span>
                  <input className="acord-input" type="number" value={data.SquareFootage ?? ''}
                    onChange={onText('SquareFootage')} readOnly={isPlaceholder} />
                </label>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">OPEN TO PUBLIC SQ FT</span>
                  <input className="acord-input" type="number" value={data.OpenToPublicArea ?? ''}
                    onChange={onText('OpenToPublicArea')} readOnly={isPlaceholder} />
                </label>
                <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">TOTAL BLDG AREA SQ FT</span>
                  <input className="acord-input" type="number" value={data.SquareFootage ?? ''} readOnly />
                </label>
              </div>
              <div className="acord-row">
                <label className="acord-cell acord-cell--fillable" style={{ flex: 4 }}>
                  <span className="acord-cell-label">DESCRIPTION OF OPERATIONS</span>
                  <input className="acord-input" value={data.BuildingDescription || ''}
                    onChange={onText('BuildingDescription')} readOnly={isPlaceholder} />
                </label>
                <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
                  <span className="acord-cell-label">ANY AREA LEASED TO OTHERS?</span>
                  <div className="acord-inline-checks">
                    <label className="acord-checkbox">
                      <input type="checkbox" checked={data.LeasedToOthers === true}
                        onChange={() => { if (!isPlaceholder) updateBuilding(idx, 'LeasedToOthers', true); }} />
                      <span>Yes</span>
                    </label>
                    <label className="acord-checkbox">
                      <input type="checkbox" checked={data.LeasedToOthers === false}
                        onChange={() => { if (!isPlaceholder) updateBuilding(idx, 'LeasedToOthers', false); }} />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* ============ NATURE OF BUSINESS ============ */}
        <div className="acord-section-header">NATURE OF BUSINESS</div>
        <div className="acord-row">
          <div className="acord-cell acord-cell--fillable" style={{ flex: 3 }}>
            <span className="acord-cell-label">TYPE OF BUSINESS</span>
            <div className="acord-inline-checks">
              {NATURE_OF_BUSINESS_TYPES.map(nob => (
                // X-style checkboxes; only one checked at a time via the
                // controlled `checked` prop matching insuredForm.NatureOfBusiness.
                <label className="acord-checkbox" key={nob}>
                  <input type="checkbox"
                    checked={insuredForm.NatureOfBusiness === nob}
                    onChange={() => setInsuredForm(f => ({ ...f, NatureOfBusiness: nob }))} />
                  <span>{nob}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">DATE BUSINESS STARTED (MM/DD/YYYY)</span>
            <input className="acord-input" value={insuredForm.DateBusinessStarted}
              onChange={e => setInsuredForm(f => ({ ...f, DateBusinessStarted: e.target.value }))} />
          </label>
        </div>
        <div className="acord-row">
          <label className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">DESCRIPTION OF PRIMARY OPERATIONS</span>
            <textarea className="acord-input acord-textarea" value={insuredForm.OperationsDescription}
              onChange={e => setInsuredForm(f => ({ ...f, OperationsDescription: e.target.value }))} />
          </label>
        </div>
        <div className="acord-row">
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">RETAIL STORES OR SERVICE OPERATIONS &mdash; % OF TOTAL SALES</span>
            {/* TODO: wire to insuredForm field when added to FIG_Insureds schema. */}
            <input className="acord-input" readOnly />
          </div>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">INSTALLATION, SERVICE OR REPAIR WORK %</span>
            <input className="acord-input" readOnly />
          </div>
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">OFF PREMISES INSTALLATION, SERVICE OR REPAIR WORK %</span>
            <input className="acord-input" readOnly />
          </div>
        </div>
        <div className="acord-row">
          <div className="acord-cell acord-cell--fillable" style={{ flex: 1 }}>
            <span className="acord-cell-label">DESCRIPTION OF OPERATIONS OF OTHER NAMED INSUREDS</span>
            {/* TODO: wire to insuredForm field when added to FIG_Insureds schema. */}
            <textarea className="acord-input acord-textarea" readOnly />
          </div>
        </div>

        {/* ============ FOOTER ============ */}
        <div className="acord-footer-line">
          <span>ACORD 125 (2025/03)</span>
          <span>Page 2 of 5</span>
          <span>© 1993-2025 ACORD CORPORATION. All rights reserved.</span>
        </div>
      </div>
      </div>
      )}

      {currentPage === 3 && <>

      {/* ============ ADDITIONAL INTEREST ============ */}
      <div className="acord-section">
        <div className="acord-section-bar">ADDITIONAL INTEREST</div>
        <div className="acord-section-body">
          {aiForms.length === 0 ? (
            <p className="acord-lob-note" style={{ margin: '0.5rem 0' }}>No additional interests — manage from Additional Interests tab.</p>
          ) : aiForms.map((ai, i) => (
            <div key={ai.id || i} style={{ borderBottom: '2px solid #bbb', paddingBottom: '0.4rem', marginBottom: '0.4rem' }}>
              <div className="acord-row">
                <div className="acord-cell acord-cell--2">
                  <span className="acord-label">INTEREST TYPE</span>
                  <div className="acord-checkbox-row acord-checkbox-row--wrap">
                    {['Additional Insured','Breach of Warranty','Co-Owner','Employee As Lessor',
                      'Leaseback Owner','Lenders Loss Payable','Lienholder','Loss Payee',
                      'Mortgagee','Owner','Registrant','Trustee'].map(t => (
                      <label className="acord-checkbox" key={t}>
                        <input type="radio" name={`aiType_${i}`}
                          checked={(ai.InterestType || '') === t}
                          onChange={() => updateAI(i, 'InterestType', t)} />
                        <span>{t}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="acord-row">
                <label className="acord-cell acord-cell--2">
                  <span className="acord-label">NAME AND ADDRESS</span>
                  <input className="acord-input" value={ai.EntityName || ''}
                    onChange={e => updateAI(i, 'EntityName', e.target.value)} />
                </label>
                <label className="acord-cell acord-cell--sm">
                  <span className="acord-label">RANK</span>
                  <input className="acord-input" value={ai.Rank || ''}
                    onChange={e => updateAI(i, 'Rank', e.target.value)} />
                </label>
              </div>
              <div className="acord-row">
                <label className="acord-cell">
                  <span className="acord-label">ADDRESS</span>
                  <input className="acord-input" value={ai.Address || ''}
                    onChange={e => updateAI(i, 'Address', e.target.value)} />
                </label>
                <label className="acord-cell acord-cell--sm">
                  <span className="acord-label">CITY</span>
                  <input className="acord-input" value={ai.City || ''}
                    onChange={e => updateAI(i, 'City', e.target.value)} />
                </label>
                <label className="acord-cell acord-cell--xs">
                  <span className="acord-label">STATE</span>
                  <input className="acord-input" value={ai.State || ''}
                    onChange={e => updateAI(i, 'State', e.target.value)} maxLength={2} />
                </label>
                <label className="acord-cell acord-cell--xs">
                  <span className="acord-label">ZIP</span>
                  <input className="acord-input" value={ai.Zip || ''}
                    onChange={e => updateAI(i, 'Zip', e.target.value)} />
                </label>
              </div>
              <div className="acord-row">
                <div className="acord-cell" style={{ flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
                  <label className="acord-checkbox">
                    <input type="checkbox" checked={ai.EvidenceRequired === true}
                      onChange={e => updateAI(i, 'EvidenceRequired', e.target.checked)} />
                    <span>Evidence</span>
                  </label>
                  <label className="acord-checkbox">
                    <input type="checkbox" checked={ai._certificate === true}
                      onChange={e => updateAI(i, '_certificate', e.target.checked)} />
                    <span>Certificate</span>
                  </label>
                  <label className="acord-checkbox">
                    <input type="checkbox" checked={ai._policy === true}
                      onChange={e => updateAI(i, '_policy', e.target.checked)} />
                    <span>Policy</span>
                  </label>
                  <label className="acord-checkbox">
                    <input type="checkbox" checked={ai._sendBill === true}
                      onChange={e => updateAI(i, '_sendBill', e.target.checked)} />
                    <span>Send Bill</span>
                  </label>
                </div>
              </div>
              <div className="acord-row">
                <label className="acord-cell acord-cell--sm">
                  <span className="acord-label">LOC #</span>
                  <input className="acord-input" value={ai.LocationNumber || ''}
                    onChange={e => updateAI(i, 'LocationNumber', e.target.value)} />
                </label>
                <label className="acord-cell acord-cell--2">
                  <span className="acord-label">ITEM DESCRIPTION</span>
                  <input className="acord-input" value={ai.ItemDescription || ''}
                    onChange={e => updateAI(i, 'ItemDescription', e.target.value)} />
                </label>
              </div>
              <div className="acord-row">
                <label className="acord-cell">
                  <span className="acord-label">REFERENCE / LOAN #</span>
                  <input className="acord-input" value={ai.ReferenceNumber || ''}
                    onChange={e => updateAI(i, 'ReferenceNumber', e.target.value)} />
                </label>
                <label className="acord-cell">
                  <span className="acord-label">PHONE</span>
                  <input className="acord-input" value={ai.Phone || ''}
                    onChange={e => updateAI(i, 'Phone', e.target.value)} />
                </label>
                <label className="acord-cell">
                  <span className="acord-label">EMAIL</span>
                  <input className="acord-input" value={ai.Email || ''}
                    onChange={e => updateAI(i, 'Email', e.target.value)} />
                </label>
              </div>
              <div className="acord-row">
                <label className="acord-cell acord-cell--2">
                  <span className="acord-label">NOTES / REASON FOR INTEREST</span>
                  <input className="acord-input" value={ai.Notes || ''}
                    onChange={e => updateAI(i, 'Notes', e.target.value)} />
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ============ GENERAL INFORMATION ============ */}
      <div className="acord-section">
        <div className="acord-section-bar">GENERAL INFORMATION</div>
        <div className="acord-section-body">
          {(() => {
            // ----- Local helpers (close over gqForms / updateGQ) -----
            const qrowStyle = { borderBottom: '1px solid #ddd', padding: '0.3rem 0' };
            const taStyle = { minHeight: '38px', resize: 'vertical' };
            const YNCell = ({ num }) => (
              <div className="acord-cell acord-cell--xs" style={{ flexDirection: 'row', gap: '0.5rem', alignItems: 'center' }}>
                <label className="acord-checkbox">
                  <input type="radio" name={`gq${num}`} checked={(gqForms[num]?.Answer || '') === 'Y'}
                    onChange={() => updateGQ(num, 'Answer', 'Y')} />
                  <span>Y</span>
                </label>
                <label className="acord-checkbox">
                  <input type="radio" name={`gq${num}`} checked={(gqForms[num]?.Answer || '') === 'N'}
                    onChange={() => updateGQ(num, 'Answer', 'N')} />
                  <span>N</span>
                </label>
              </div>
            );
            // Q2 / Q5 share the comma-string-as-Set pattern. Q5 ALSO has a
            // free-text occurrence description; we pipe-delimit the two parts
            // inside a single Explanation field: "checkbox state | free text".
            // Legacy data without a pipe is treated as checkbox state only.
            const toggleSetItem = (qNum, opt, currentStr) => {
              const items = (currentStr || '').split(',').map(s => s.trim()).filter(Boolean);
              const idx = items.indexOf(opt);
              if (idx >= 0) items.splice(idx, 1); else items.push(opt);
              return items.join(', ');
            };
            const splitQ5 = () => {
              const raw = gqForms['5']?.Explanation || '';
              const parts = raw.split('|');
              return [(parts[0] || '').trim(), parts.slice(1).join('|').trim()];
            };
            const writeQ5 = (cbStr, txtStr) => {
              const left = (cbStr || '').trim();
              const right = (txtStr || '').trim();
              const out = right ? `${left} | ${right}` : left;
              updateGQ('5', 'Explanation', out);
            };
            const [q5Checkboxes, q5Text] = splitQ5();

            return (
              <>
                {/* Q1a — Subsidiary of another entity */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">1a. IS THE APPLICANT A SUBSIDIARY OF ANOTHER ENTITY?</span></div>
                    <YNCell num="1a" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">PARENT COMPANY NAME / RELATIONSHIP / % OWNED</span>
                      <input className="acord-input"
                        placeholder="e.g. ABC Holdings, Parent Company, 100% owned"
                        value={gqForms['1a']?.Explanation || ''}
                        onChange={e => updateGQ('1a', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q1b — Has subsidiaries */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">1b. DOES THE APPLICANT HAVE ANY SUBSIDIARIES?</span></div>
                    <YNCell num="1b" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">SUBSIDIARY COMPANY NAME(S) / RELATIONSHIP / % OWNED</span>
                      <input className="acord-input"
                        placeholder="e.g. XYZ LLC, Wholly-owned subsidiary, 100%"
                        value={gqForms['1b']?.Explanation || ''}
                        onChange={e => updateGQ('1b', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q2 — Formal safety program (existing comma-string pattern preserved) */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">2. IS A FORMAL SAFETY PROGRAM IN OPERATION?</span></div>
                    <YNCell num="2" />
                  </div>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2" style={{ flexDirection: 'row', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {['Safety Manual', 'Safety Position', 'Monthly Meetings', 'OSHA Compliance'].map(opt => (
                        <label className="acord-checkbox" key={opt}>
                          <input type="checkbox"
                            checked={(gqForms['2']?.Explanation || '').includes(opt)}
                            onChange={() => updateGQ('2', 'Explanation', toggleSetItem('2', opt, gqForms['2']?.Explanation))} />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Q3 — Flammables / explosives / chemicals */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">3. ANY EXPOSURE TO FLAMMABLES, EXPLOSIVES, OR CHEMICALS?</span></div>
                    <YNCell num="3" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">DESCRIBE EXPOSURE</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['3']?.Explanation || ''}
                        onChange={e => updateGQ('3', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q4 — Other insurance with this company */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">4. ANY OTHER INSURANCE WITH THIS COMPANY? (List policy numbers.)</span></div>
                    <YNCell num="4" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">LIST LINE OF BUSINESS AND POLICY NUMBER(S)</span>
                      <textarea className="acord-input" style={taStyle}
                        placeholder="e.g. Commercial Auto - POL12345; Workers Comp - WC67890"
                        value={gqForms['4']?.Explanation || ''}
                        onChange={e => updateGQ('4', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q5 — Cancellation / non-renewal — pipe-delimited Explanation:
                    "<reason checkboxes> | <occurrence description>" */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">5. ANY POLICY OR COVERAGE DECLINED, CANCELLED OR NON-RENEWED DURING THE PRIOR THREE (3) YEARS FOR ANY PREMISES OR OPERATIONS?</span></div>
                    <YNCell num="5" />
                  </div>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2" style={{ flexDirection: 'row', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {['Non-Payment', 'Non-Renewal', 'Agent No Longer Represents Carrier', 'Underwriting', 'Condition Corrected'].map(opt => (
                        <label className="acord-checkbox" key={opt}>
                          <input type="checkbox"
                            checked={q5Checkboxes.split(',').map(s => s.trim()).includes(opt)}
                            onChange={() => writeQ5(toggleSetItem('5', opt, q5Checkboxes), q5Text)} />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">IF YES, DESCRIBE OCCURRENCE(S)</span>
                      <textarea className="acord-input" style={taStyle}
                        placeholder="Date: ___  Explanation: ___  Resolution: ___  Resolve Date: ___"
                        value={q5Text}
                        onChange={e => writeQ5(q5Checkboxes, e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q6 — Past losses (sexual abuse / discrimination / negligent hiring) */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">6. ANY PAST LOSSES OR CLAIMS RELATING TO SEXUAL ABUSE OR MOLESTATION ALLEGATIONS, DISCRIMINATION OR NEGLIGENT HIRING?</span></div>
                    <YNCell num="6" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">IF YES, DESCRIBE OCCURRENCE(S)</span>
                      <textarea className="acord-input" style={taStyle}
                        placeholder="Date: ___  Description: ___  Resolution: ___  Resolve Date: ___"
                        value={gqForms['6']?.Explanation || ''}
                        onChange={e => updateGQ('6', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q7 — Indictment / conviction (last 5 years) */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">7. DURING THE LAST FIVE YEARS, HAS ANY APPLICANT BEEN INDICTED FOR OR CONVICTED OF FRAUD, BRIBERY, ARSON, ETC.?</span></div>
                    <YNCell num="7" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">EXPLAIN</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['7']?.Explanation || ''}
                        onChange={e => updateGQ('7', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q8 — Uncorrected fire / safety code violations */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">8. ANY UNCORRECTED FIRE AND/OR SAFETY CODE VIOLATIONS?</span></div>
                    <YNCell num="8" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">EXPLAIN</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['8']?.Explanation || ''}
                        onChange={e => updateGQ('8', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q9 — Foreclosure / repossession / bankruptcy */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">9. HAS THE APPLICANT HAD A FORECLOSURE FILED AGAINST THEM, FORECLOSURE, REPOSSESSION, BANKRUPTCY?</span></div>
                    <YNCell num="9" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">EXPLAIN</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['9']?.Explanation || ''}
                        onChange={e => updateGQ('9', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q10 — Judgement / lien (last 5 years) */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">10. HAS APPLICANT HAD A JUDGEMENT OR LIEN DURING THE LAST FIVE YEARS?</span></div>
                    <YNCell num="10" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">EXPLAIN</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['10']?.Explanation || ''}
                        onChange={e => updateGQ('10', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q11 — Trust */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">11. HAS BUSINESS BEEN PLACED IN A TRUST? NAME OF TRUST:</span></div>
                    <YNCell num="11" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">NAME OF TRUST</span>
                      <input className="acord-input"
                        value={gqForms['11']?.Explanation || ''}
                        onChange={e => updateGQ('11', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q12 — Foreign operations / products */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">12. ANY FOREIGN OPERATIONS, FOREIGN PRODUCTS DISTRIBUTED IN USA, OR US PRODUCTS SOLD/DISTRIBUTED IN FOREIGN COUNTRIES? (If YES, attach ACORD 815/816)</span></div>
                    <YNCell num="12" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">DESCRIBE</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['12']?.Explanation || ''}
                        onChange={e => updateGQ('12', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q13 — Other business ventures not requesting coverage */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">13. DOES APPLICANT HAVE OTHER BUSINESS VENTURES FOR WHICH COVERAGE IS NOT REQUESTED?</span></div>
                    <YNCell num="13" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">DESCRIBE OTHER BUSINESS VENTURES</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['13']?.Explanation || ''}
                        onChange={e => updateGQ('13', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q14 — Drones (own / lease / operate) */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">14. DOES APPLICANT OWN/LEASE/OPERATE ANY DRONES? (If "YES", describe use)</span></div>
                    <YNCell num="14" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">DESCRIBE DRONE USE</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['14']?.Explanation || ''}
                        onChange={e => updateGQ('14', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>

                {/* Q15 — Hire others to operate drones */}
                <div style={qrowStyle}>
                  <div className="acord-row">
                    <div className="acord-cell acord-cell--2"><span className="acord-label">15. DOES APPLICANT HIRE OTHERS TO OPERATE DRONES? (If "YES", describe use)</span></div>
                    <YNCell num="15" />
                  </div>
                  <div className="acord-row">
                    <label className="acord-cell acord-cell--2">
                      <span className="acord-label">DESCRIBE THIRD-PARTY DRONE USE</span>
                      <textarea className="acord-input" style={taStyle}
                        value={gqForms['15']?.Explanation || ''}
                        onChange={e => updateGQ('15', 'Explanation', e.target.value)} />
                    </label>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>{pageNav}</div>
      </>}

      {currentPage === 4 && <>

      {/* ============ PRIOR CARRIER INFORMATION ============ */}
      <div className="acord-section">
        <div className="acord-section-bar">PRIOR CARRIER INFORMATION</div>
        <div className="acord-section-body">
          {carrierForms.length === 0 ? (
            <p className="acord-lob-note" style={{ margin: '0.5rem 0' }}>No prior carrier records.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: '#eef2f7' }}>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>YEAR</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>LINE OF BUSINESS</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>CARRIER</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>POLICY #</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>PREMIUM</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>EFF DATE</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>EXP DATE</th>
                  </tr>
                </thead>
                <tbody>
                  {carrierForms.map((c, i) => (
                    <tr key={c.id || i} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={c.PolicyYear || ''}
                          onChange={e => updateCarrier(i, 'PolicyYear', e.target.value)} style={{ width: '4rem' }} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={c.LOB || ''}
                          onChange={e => updateCarrier(i, 'LOB', e.target.value)} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={c.Carrier || ''}
                          onChange={e => updateCarrier(i, 'Carrier', e.target.value)} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={c.PolicyNumber || ''}
                          onChange={e => updateCarrier(i, 'PolicyNumber', e.target.value)} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" type="number" value={c.Premium ?? ''}
                          onChange={e => updateCarrier(i, 'Premium', e.target.value)} style={{ width: '5rem' }} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={c.EffectiveDate || ''}
                          onChange={e => updateCarrier(i, 'EffectiveDate', e.target.value)} style={{ width: '6rem' }} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={c.ExpirationDate || ''}
                          onChange={e => updateCarrier(i, 'ExpirationDate', e.target.value)} style={{ width: '6rem' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ============ LOSS HISTORY ============ */}
      <div className="acord-section">
        <div className="acord-section-bar">LOSS HISTORY</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <div className="acord-cell" style={{ flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
              <label className="acord-checkbox">
                <input type="checkbox" checked={noLosses}
                  onChange={e => setNoLosses(e.target.checked)} />
                <span>CHECK IF NO PRIOR LOSSES</span>
              </label>
            </div>
            <label className="acord-cell acord-cell--sm">
              <span className="acord-label">NUMBER OF YEARS</span>
              <input className="acord-input" value={lossYears}
                onChange={e => setLossYears(e.target.value)} style={{ width: '3rem' }} />
            </label>
            <label className="acord-cell acord-cell--sm">
              <span className="acord-label">TOTAL LOSSES</span>
              <input className="acord-input" readOnly
                value={lossForms.reduce((sum, l) => sum + (Number(l.AmountPaid) || 0) + (Number(l.AmountReserved) || 0), 0).toLocaleString()} />
            </label>
          </div>

          {!noLosses && lossForms.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: '0.4rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: '#eef2f7' }}>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>DATE OF OCC</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>LINE</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>DESCRIPTION</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>DATE OF CLAIM</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>AMT PAID</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>AMT RESERVED</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>SUBR</th>
                    <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>OPEN</th>
                  </tr>
                </thead>
                <tbody>
                  {lossForms.map((l, i) => (
                    <tr key={l.id || i} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={l.DateOfOccurrence || ''}
                          onChange={e => updateLoss(i, 'DateOfOccurrence', e.target.value)} style={{ width: '6rem' }} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={l.LOB || ''}
                          onChange={e => updateLoss(i, 'LOB', e.target.value)} style={{ width: '5rem' }} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={l.Description || ''}
                          onChange={e => updateLoss(i, 'Description', e.target.value)} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" value={l.DateOfClaim || ''}
                          onChange={e => updateLoss(i, 'DateOfClaim', e.target.value)} style={{ width: '6rem' }} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" type="number" value={l.AmountPaid ?? ''}
                          onChange={e => updateLoss(i, 'AmountPaid', e.target.value)} style={{ width: '5rem' }} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <input className="acord-input" type="number" value={l.AmountReserved ?? ''}
                          onChange={e => updateLoss(i, 'AmountReserved', e.target.value)} style={{ width: '5rem' }} />
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem', textAlign: 'center' }}>
                        <label className="acord-checkbox">
                          <input type="checkbox" checked={l.Subrogation === true}
                            onChange={e => updateLoss(i, 'Subrogation', e.target.checked)} />
                          <span>Y</span>
                        </label>
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem', textAlign: 'center' }}>
                        <label className="acord-checkbox">
                          <input type="checkbox" checked={l.ClaimOpen === true}
                            onChange={e => updateLoss(i, 'ClaimOpen', e.target.checked)} />
                          <span>Y</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!noLosses && lossForms.length === 0 && (
            <p className="acord-lob-note" style={{ margin: '0.5rem 0' }}>No loss records.</p>
          )}
        </div>
      </div>

      {/* ============ REMARKS ============ */}
      <div className="acord-section">
        <div className="acord-section-bar">REMARKS / PROCESSING INSTRUCTIONS</div>
        <div className="acord-section-body">
          <textarea className="acord-input" rows={6} value={remarks}
            onChange={e => setRemarks(e.target.value)}
            style={{ resize: 'vertical', minHeight: '5rem', padding: '0.4rem' }}
            placeholder="Enter remarks or special processing instructions..." />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>{pageNav}</div>
      </>}

      {currentPage === 5 && <>

      {/* ============ SIGNATURE ============ */}
      <div className="acord-section">
        <div className="acord-section-bar">APPLICANT'S SIGNATURE</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <label className="acord-cell acord-cell--2">
              <span className="acord-label">APPLICANT'S SIGNATURE</span>
              <input className="acord-input" value={insuredForm.CompanyName}
                readOnly style={{ fontStyle: 'italic' }} />
            </label>
            <label className="acord-cell">
              <span className="acord-label">DATE</span>
              <input className="acord-input" value={today} readOnly />
            </label>
          </div>
        </div>
      </div>

      <div className="acord-section">
        <div className="acord-section-bar">PRODUCER'S SIGNATURE</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <label className="acord-cell acord-cell--2">
              <span className="acord-label">PRODUCER SIGNATURE</span>
              <input className="acord-input" value={agencyForm.ProducerName}
                onChange={e => setAgencyForm(f => ({ ...f, ProducerName: e.target.value }))}
                style={{ fontStyle: 'italic' }} />
            </label>
            <label className="acord-cell">
              <span className="acord-label">DATE</span>
              <input className="acord-input" value={today} readOnly />
            </label>
          </div>
          <div className="acord-row">
            <label className="acord-cell">
              <span className="acord-label">PRODUCER NAME (PLEASE PRINT)</span>
              <input className="acord-input" value={agencyForm.ProducerName}
                onChange={e => setAgencyForm(f => ({ ...f, ProducerName: e.target.value }))} />
            </label>
            <label className="acord-cell">
              <span className="acord-label">STATE PRODUCER LICENSE NO.</span>
              <input className="acord-input" value={agencyForm.StateLicenseNumber}
                onChange={e => setAgencyForm(f => ({ ...f, StateLicenseNumber: e.target.value }))} />
            </label>
            <label className="acord-cell">
              <span className="acord-label">NATIONAL PRODUCER NUMBER</span>
              <input className="acord-input" value={agencyForm.NationalProducerNumber}
                onChange={e => setAgencyForm(f => ({ ...f, NationalProducerNumber: e.target.value }))} />
            </label>
          </div>
        </div>
      </div>

      {/* ============ FRAUD WARNING ============ */}
      <div className="acord-section">
        <div className="acord-section-bar">FRAUD WARNINGS</div>
        <div className="acord-section-body" style={{ fontSize: '0.65rem', color: '#666', lineHeight: 1.6 }}>
          <p style={{ margin: '0.3rem 0' }}><strong>ANY PERSON WHO KNOWINGLY AND WITH INTENT TO DEFRAUD ANY INSURANCE COMPANY OR OTHER PERSON FILES AN APPLICATION FOR INSURANCE OR STATEMENT OF CLAIM CONTAINING ANY MATERIALLY FALSE INFORMATION, OR CONCEALS FOR THE PURPOSE OF MISLEADING, INFORMATION CONCERNING ANY FACT MATERIAL THERETO, COMMITS A FRAUDULENT INSURANCE ACT, WHICH IS A CRIME AND SUBJECTS THE PERSON TO CRIMINAL AND CIVIL PENALTIES.</strong></p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN ALABAMA:</strong> Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or who knowingly presents false information in an application for insurance is guilty of a crime and may be subject to restitution fines or confinement in prison, or any combination thereof.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN ARKANSAS, LOUISIANA, RHODE ISLAND AND WEST VIRGINIA:</strong> Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or knowingly presents false information in an application for insurance is guilty of a crime and may be subject to fines and confinement in prison.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN COLORADO:</strong> It is unlawful to knowingly provide false, incomplete, or misleading facts or information to an insurance company for the purpose of defrauding or attempting to defraud the company. Penalties may include imprisonment, fines, denial of insurance, and civil damages.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN THE DISTRICT OF COLUMBIA, HAWAII, IDAHO, INDIANA, KENTUCKY, MINNESOTA, NEW MEXICO, OHIO, OKLAHOMA AND OREGON:</strong> WARNING: Any person who knowingly, and with intent to injure, defraud or deceive any insurer, makes any claim for the proceeds of an insurance policy containing any false, incomplete or misleading information is guilty of a felony.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN FLORIDA AND MEMPHIS, TENNESSEE:</strong> Any person who knowingly and with intent to injure, defraud, or deceive any insurer files a statement of claim or an application containing any false, incomplete, or misleading information is guilty of a felony of the third degree.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN KANSAS:</strong> Any person who, knowingly and with intent to defraud, presents, causes to be presented or prepares with knowledge or belief that it will be presented to or by an insurer, purported insurer, broker or any agent thereof, any written statement as part of, or in support of, an application for the issuance of, or the rating of an insurance policy for personal or commercial insurance, or a claim for payment or other benefit pursuant to an insurance policy for commercial or personal insurance which such person knows to contain materially false information concerning any fact material thereto; or conceals, for the purpose of misleading, information concerning any fact material thereto commits a fraudulent insurance act.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN MAINE, TENNESSEE, VIRGINIA AND WASHINGTON:</strong> It is a crime to knowingly provide false, incomplete or misleading information to an insurance company for the purpose of defrauding the company. Penalties include imprisonment, fines and denial of insurance benefits.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN MARYLAND:</strong> Any person who knowingly or willfully presents a false or fraudulent claim for payment of a loss or benefit or who knowingly or willfully presents false information in an application for insurance is guilty of a crime and may be subject to fines and confinement in prison.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN NEW JERSEY:</strong> Any person who includes any false or misleading information on an application for an insurance policy is subject to criminal and civil penalties.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN NEW YORK:</strong> Any person who knowingly and with intent to defraud any insurance company or other person files an application for insurance or statement of claim containing any materially false information, or conceals for the purpose of misleading, information concerning any fact material thereto, commits a fraudulent insurance act, which is a crime, and shall also be subject to a civil penalty not to exceed five thousand dollars and the stated value of the claim for each such violation.</p>
          <p style={{ margin: '0.3rem 0' }}><strong>APPLICABLE IN PENNSYLVANIA:</strong> Any person who knowingly and with intent to defraud any insurance company or other person files an application for insurance or statement of claim containing any materially false information or conceals for the purpose of misleading, information concerning any fact material thereto commits a fraudulent insurance act, which is a crime and subjects such person to criminal and civil penalties.</p>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>{pageNav}</div>
      </>}

      {/* ============ SAVE ============ */}
      <div className="acord-actions">
        <button className="FIG-back-btn" onClick={onBack}>&lsaquo; Back to ACORDs</button>
        <button className="FIG-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save All'}
        </button>
        <button className="FIG-save-btn acord-pdf-btn" disabled={generating} onClick={async () => {
          setGenerating(true);
          try {
            const selectedPolicy = policies[selectedPolicyIdx];
            const policyForPdf = selectedPolicy
              ? { ...selectedPolicy, ...policyForm }
              : null;
            await generateACORD125({
              insured: { ...insured, ...insuredForm },
              agency: agencyForm,
              policy: policyForPdf,
              transactionStatus,
              getToken,
              checkedLOBs: [...checkedLOBs],
            });
          } catch (err) {
            console.error('[ACORD 125] PDF generation failed:', err);
            setSaveMsg('Error generating PDF: ' + err.message);
          } finally {
            setGenerating(false);
          }
        }}>
          {generating ? 'Generating...' : 'Generate PDF'}
        </button>
        {saveMsg && (
          <span className={'FIG-save-msg' + (saveMsg.startsWith('Error') ? ' FIG-save-msg--error' : '')}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
}

export default ACORD125Page1;
